// Shared Jodit editor configuration and print helper used by the customer
// letter/email views (AgentEmailView, LWTEmailView) and the template editor.

import Alert from "react-s-alert";

// Build the @page geometry for the browser print path. marginVertical (top/bottom)
// and marginHorizontal (left/right) are in inches; non-positive values fall back to
// the 1in default. Keep this in sync with the server PDF path in handlers/util.go
// (buildPrintDocument); both must produce identical US Letter output - TS-17667.
export function printPageCSS(marginVertical = 1, marginHorizontal = 1) {
  const v = marginVertical > 0 ? marginVertical : 1;
  const h = marginHorizontal > 0 ? marginHorizontal : 1;
  return `@page { size: letter; margin: ${v}in ${h}in; } body { margin: 0; }`;
}

// Default 1in geometry, kept for callers that do not customize margins.
export const PRINT_PAGE_CSS = printPageCSS(1, 1);

// Print arbitrary HTML through a hidden, letter-sized iframe, using the given page
// margins (inches; default 1in). Returns true if the print dialog opened, false if not.
export function printHTML(html, marginVertical = 1, marginHorizontal = 1) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0';
  document.body.appendChild(iframe);
  const cleanup = () => { if (iframe.parentNode) document.body.removeChild(iframe); };
  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('iframe contentDocument unavailable');
    // The @page <style> is emitted AFTER the letter body, not in <head>: some saved
    // templates embed their own @page rule, and CSS @page declarations cascade last-wins,
    // so a rule in <head> loses to the template's and the selected margins are ignored.
    // Declaring ours last keeps the browser print preview in sync with the server PDF -
    // mirrors buildPrintDocument in handlers/util.go (TS-17667).
    doc.write(
      '<!DOCTYPE html><html><head><title>Print</title></head><body>' +
      html +
      '<style>' + printPageCSS(marginVertical, marginHorizontal) + '</style>' +
      '</body></html>'
    );
    doc.close();
    iframe.contentWindow.focus();
    iframe.contentWindow.onafterprint = cleanup;
    iframe.contentWindow.print();
    return true;
  } catch (e) {
    cleanup();
    Alert.error('Unable to open the print dialog. Please try again.');
    return false;
  }
}

// Print an already-generated PDF (e.g. the server-rendered letter that was
// uploaded to S3) so the printed copy is the exact stored PDF rather than a
// browser re-render of the source HTML.
//
// The PDF is fetched fully into a blob first, then printed from a same-origin
// blob: URL. Printing the streamed download URL directly proved unreliable in
// practice: the iframe fired `load` before the byte-range stream finished, so
// print() hit a not-yet-ready PDF viewer and threw (aborting the in-flight
// request). A fully-buffered blob has no streaming, so the viewer is ready
// when print() fires.
//
// Loading is asynchronous, so onPrinted (optional) is invoked after print() is
// called — because the print dialog is modal, effectively after it is dismissed
// — rather than via a return value.
//
// Print-event behavior for embedded PDFs is browser/version dependent, so
// neither `load` nor `afterprint` is guaranteed to fire. We add a load
// timeout, an `onerror` handler, and a backstop cleanup so a missing event
// never leaks the iframe/blob URL or leaves the user with no feedback.
export function printPDF(url, onPrinted) {
  if (!url) {
    Alert.error('Unable to print: the document link is missing.');
    return;
  }
  fetch(url, { credentials: 'same-origin' })
    .then((res) => {
      if (!res.ok) {
        throw new Error('Failed to load document (HTTP ' + res.status + ')');
      }
      return res.blob();
    })
    .then((blob) => {
      const blobURL = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;width:0;height:0;border:0';

      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
        URL.revokeObjectURL(blobURL);
      };

      const loadTimer = setTimeout(() => {
        if (cleaned) return;
        cleanup();
        Alert.error('Unable to open the print dialog. Please try again.');
      }, 15000);

      iframe.onload = () => {
        clearTimeout(loadTimer);
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.onafterprint = cleanup;
          iframe.contentWindow.print();
        } catch (e) {
          cleanup();
          Alert.error('Unable to open the print dialog. Please try again.');
          return;
        }
        setTimeout(cleanup, 60000);
        if (typeof onPrinted === 'function') {
          onPrinted();
        }
      };
      iframe.onerror = () => {
        clearTimeout(loadTimer);
        cleanup();
        Alert.error('Unable to load the document for printing. Please try again.');
      };
      iframe.src = blobURL;
      document.body.appendChild(iframe);
    })
    .catch((e) => {
      /*eslint no-console: 0*/
      console.error('printPDF failed:', e);
      Alert.error('Unable to load the document for printing. Please try again.');
    });
}

const baseEditorConfig = {
  readonly: false,
  height: 500,
  toolbarSticky: false,
  disablePlugins: ['print'],
  buttons: [
    'bold', 'italic', 'underline', 'strikethrough', '|',
    'ul', 'ol', '|',
    'outdent', 'indent', '|',
    'font', 'fontsize', 'brush', 'paragraph', '|',
    'link', 'table', '|',
    'align', 'undo', 'redo', '|',
    'hr', 'eraser', 'copyformat', '|',
    'fullsize', 'customPrint'
  ]
};

// Build an editor config whose Print button honors the current page margins.
// getMargins() is called at print time and should return
// { marginVertical, marginHorizontal } (inches); omit it for the 1in default.
export function makeEditorConfig(getMargins) {
  return {
    ...baseEditorConfig,
    extraButtons: [
      {
        name: 'customPrint',
        icon: 'print',
        tooltip: 'Print',
        exec(editor) {
          const m = (typeof getMargins === 'function' && getMargins()) || {};
          printHTML(editor.value, m.marginVertical, m.marginHorizontal);
        }
      }
    ]
  };
}

// Default config (1in margins) for callers that do not customize margins.
export const editorConfig = makeEditorConfig();
